import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Trash2, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { useAuth } from "../App";
import apiClient from "../lib/api";
import { toastApiError } from "../lib/toastApiError";

const DeleteAccountSection = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { t } = useTranslation("pharmacy");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    setLoading(true);
    try {
      await apiClient.delete("/auth/account");
      toast.success(t("deleteAccount.success"));
      logout();
      navigate("/");
    } catch (error) {
      toastApiError(error);
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  return (
    <Card className="border-red-200 bg-red-50/30">
      <CardHeader>
        <CardTitle className="text-red-700 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" />
          {t("deleteAccount.title")}
        </CardTitle>
        <CardDescription className="text-red-600">
          {t("deleteAccount.description")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button
          variant="destructive"
          onClick={() => setOpen(true)}
          data-testid="delete-account-btn"
          className="rounded-xl"
        >
          <Trash2 className="h-4 w-4 mr-2" />
          {t("deleteAccount.button")}
        </Button>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-700">{t("deleteAccount.confirmTitle")}</DialogTitle>
              <DialogDescription>
                {t("deleteAccount.confirmDesc")}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={loading}
                data-testid="cancel-delete-btn"
              >
                {t("actions.cancel")}
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={loading}
                data-testid="confirm-delete-btn"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  t("actions.confirmDelete")
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default DeleteAccountSection;
